import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientImplantDto, patientImplantFormData } from "../types/patientImplant";
import { patientImplantService } from "../services/patientImplantService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const usePatientImplants = (overridePatientId?: string) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();
  const patientId = overridePatientId ?? urlPatientId;

  // ΕΝΑ ΚΑΙ ΜΟΝΑΔΙΚΟ request: Επιλέγει αυτόματα endpoint ανάλογα με το αν υπάρχει patientId
  const {
    data: implants = [],
    loading: isFetching,
    error: getError,
    refetch,
    invalidateCache,
  } = useApi<PatientImplantDto[]>(
    patientId ? `patient-implants-${patientId}` : "patient-implants-all",
    async (signal) => {
      if (patientId) {
        return patientImplantService.getByPatientId(patientId, signal);
      }
      return patientImplantService.getAll({}, signal);
    },
    [patientId]
  );

  // Mutation για Create (προσθέτει αυτόματα το patientId από το URL στο payload)
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newPatientImplant: typeof patientImplantFormData) => {
    const payload = patientId 
      ? { ...newPatientImplant, patientId: Number(patientId) } 
      : newPatientImplant;
      
    return patientImplantService.create(payload as any);
  });
// Για το UPDATE στο hook σου:
const {
  execute: updateExecution,
  loading: isUpdating,
  error: updateError,
} = useMutation(({ id, data }: { id: number | string; data: PatientImplantDto }) =>
  patientImplantService.update(id, data)
);
  // Mutation για Delete
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientImplantService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatientImplant = async (newPatientImplant: typeof patientImplantFormData): Promise<void> => {
    try {
      await createExecution(newPatientImplant);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το εμφύτευμα ασθενούς αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updatePatientImplant = async (id: number | string, updatedData: PatientImplantDto): Promise<void> => {
      try {
        await updateExecution({ id, data: updatedData });
        invalidateCache();
        setModalState({
          isOpen: true,
          title: "Επιτυχία",
          message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
          type: "success",
        });
        refetch(true);
      } catch (err: any) {
        const errorMsg = err.message || "Σφάλμα ενημέρωσης";
        setModalState({
          isOpen: true,
          title: "Σφάλμα",
          message: errorMsg,
          type: "error",
        });
      }
    };

  const deletePatientImplants = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    patientImplants: implants, // Τα δεδομένα (είτε για έναν ασθενή, είτε όλα)
    allImplants: implants,     // Alias για συμβατότητα
    isLoading: isFetching || isCreating || isDeleting,
    error: getError || createError || deleteError,
    fetchPatientImplants: () => refetch(true),
    fetchImplants: () => refetch(true),
    createPatientImplant,
    updatePatientImplant,
    deletePatientImplants,
    modalState,
    closeModal,
  };
};